import { Link, useNavigate } from 'react-router-dom'
import { useState, useEffect, useRef } from 'react'
import { Menu, X, User, LogOut } from 'lucide-react'
import { useAuth } from '../../contexts/AuthContext'
import { Button } from './button'
import { OptimizedLogo } from './OptimizedImage'

export default function Navbar() {
  const [isOpen, setIsOpen] = useState(false)
  const [_dropdownOpen] = useState(false)
  const { user, isAuthenticated, logout } = useAuth()
  const navigate = useNavigate()
  const menuRef = useRef<HTMLDivElement>(null)

  const handleLogout = () => {
    logout()
    setIsOpen(false) // Close menu on logout
    navigate('/')
  }

  // Close mobile menu when a link is clicked
  const handleMobileMenuClick = () => {
    setIsOpen(false)
  }

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setIsOpen(false)
      }
    }

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside)
      return () => {
        document.removeEventListener('mousedown', handleClickOutside)
      }
    }
  }, [isOpen])

  return (
    <nav className="fixed top-0 left-0 right-0 bg-hawk-dark/95 backdrop-blur-md border-b border-hawk-dark-accent/50 z-50 shadow-xl">
      <div ref={menuRef} className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16">
          {/* Logo */}
          <Link to="/" className="flex items-center space-x-1">
            <div className="h-20 w-20 transition-transform duration-300 hover:scale-105 mt-2">
              <OptimizedLogo className="h-full w-full object-contain" />
            </div>
            <span className="font-bold text-xl bg-gradient-to-r from-aurora-teal to-aurora-cyan bg-clip-text text-transparent hidden sm:block">Ticket Hauk</span>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center space-x-8 min-h-[40px]">
            {isAuthenticated ? (
              <>
                <Link to="/dashboard" className="text-gray-300 hover:text-aurora-teal transition-all font-medium hover:drop-shadow-[0_0_8px_rgba(20,184,166,0.5)]">
                  Dashboard
                </Link>
                <Link to="/search" className="text-gray-300 hover:text-aurora-teal transition-all font-medium hover:drop-shadow-[0_0_8px_rgba(20,184,166,0.5)]">
                  Search Events
                </Link>
                <Link to="/alerts" className="text-gray-300 hover:text-aurora-teal transition-all font-medium hover:drop-shadow-[0_0_8px_rgba(20,184,166,0.5)]">
                  Alerts
                </Link>
                <Link to="/subscription" className="text-gray-300 hover:text-aurora-teal transition-all font-medium hover:drop-shadow-[0_0_8px_rgba(20,184,166,0.5)]">
                  Subscription
                </Link>
                <div className="relative group">
                  <button className="flex items-center space-x-2 text-gray-300 hover:text-aurora-teal transition-all font-medium">
                    {user?.avatar ? (
                      <img 
                        src={user.avatar} 
                        alt={user.name} 
                        className="w-8 h-8 rounded-full object-cover border border-hawk-dark-accent"
                      />
                    ) : (
                      <div className="w-8 h-8 rounded-full bg-gradient-to-br from-aurora-teal to-aurora-cyan flex items-center justify-center">
                        <User size={16} className="text-white" />
                      </div>
                    )}
                    <span>{user?.name}</span>
                  </button>
                  <div className="absolute right-0 mt-2 w-48 bg-hawk-dark-soft rounded-lg shadow-xl border border-hawk-dark-accent/50 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all">
                    <Link to="/profile" className="block px-4 py-2 text-gray-300 hover:bg-hawk-dark-accent hover:text-aurora-teal">
                      Profile
                    </Link>
                    <button 
                      onClick={handleLogout}
                      className="w-full text-left px-4 py-2 text-gray-300 hover:bg-hawk-dark-accent hover:text-aurora-teal flex items-center space-x-2"
                    >
                      <LogOut size={16} />
                      <span>Logout</span>
                    </button>
                  </div>
                </div>
              </>
            ) : (
              <>
                <Link to="/features">
                  <Button variant="ghost" className="text-gray-300 hover:text-aurora-teal hover:bg-hawk-dark-accent/50 transition-all">Features</Button>
                </Link>
                <Link to="/how-it-works">
                  <Button variant="ghost" className="text-gray-300 hover:text-aurora-teal hover:bg-hawk-dark-accent/50 transition-all">How It Works</Button>
                </Link>
                <Link to="/pricing">
                  <Button variant="ghost" className="text-gray-300 hover:text-aurora-teal hover:bg-hawk-dark-accent/50 transition-all">Pricing</Button>
                </Link>
                <div className="flex items-center space-x-4">
                  <Link to="/search">
                    <Button variant="ghost" className="text-gray-300 hover:text-aurora-cyan hover:bg-hawk-dark-accent/50 transition-all">Event Search</Button>
                  </Link>
                  <Link to="/login">
                    <Button className="bg-gradient-to-r from-aurora-teal to-aurora-cyan hover:from-aurora-cyan hover:to-aurora-teal text-white font-semibold px-6 py-2 rounded-lg shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-105">Sign In</Button>
                  </Link>
                </div>
              </>
            )}
          </div>

          {/* Mobile menu button */}
          <button
            onClick={() => setIsOpen(!isOpen)}
            className="md:hidden p-2 rounded-lg text-gray-300 hover:bg-hawk-dark-accent/50"
          >
            {isOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>

        {/* Mobile Navigation */}
        {isOpen && (
          <div className="md:hidden py-4 border-t border-hawk-dark-accent/50">
            {isAuthenticated ? (
              <div className="space-y-2">
                {/* User info with avatar */}
                <div className="flex items-center space-x-3 py-2 px-2 border-b border-hawk-dark-accent/30">
                  {user?.avatar ? (
                    <img 
                      src={user.avatar} 
                      alt={user.name} 
                      className="w-10 h-10 rounded-full object-cover border border-hawk-dark-accent"
                    />
                  ) : (
                    <div className="w-10 h-10 rounded-full bg-gradient-to-br from-aurora-teal to-aurora-cyan flex items-center justify-center">
                      <User size={20} className="text-white" />
                    </div>
                  )}
                  <div>
                    <p className="text-white font-medium">{user?.name}</p>
                    <p className="text-gray-400 text-sm">{user?.email}</p>
                  </div>
                </div>
                
                <Link to="/dashboard" className="block py-2 text-gray-300 hover:text-aurora-teal" onClick={handleMobileMenuClick}>
                  Dashboard
                </Link>
                <Link to="/search" className="block py-2 text-gray-300 hover:text-aurora-teal" onClick={handleMobileMenuClick}>
                  Search Events
                </Link>
                <Link to="/alerts" className="block py-2 text-gray-300 hover:text-aurora-teal" onClick={handleMobileMenuClick}>
                  Alerts
                </Link>
                <Link to="/subscription" className="block py-2 text-gray-300 hover:text-aurora-teal" onClick={handleMobileMenuClick}>
                  Subscription
                </Link>
                <Link to="/profile" className="block py-2 text-gray-300 hover:text-aurora-teal" onClick={handleMobileMenuClick}>
                  Profile
                </Link>
                <button 
                  onClick={handleLogout}
                  className="block w-full text-left py-2 text-gray-300 hover:text-aurora-teal"
                >
                  Logout
                </button>
              </div>
            ) : (
              <div className="space-y-2">
                <Link to="/features" className="block" onClick={handleMobileMenuClick}>
                  <Button variant="ghost" className="w-full justify-start text-gray-300 hover:text-aurora-teal hover:bg-hawk-dark-accent/50 transition-all">Features</Button>
                </Link>
                <Link to="/how-it-works" className="block" onClick={handleMobileMenuClick}>
                  <Button variant="ghost" className="w-full justify-start text-gray-300 hover:text-aurora-teal hover:bg-hawk-dark-accent/50 transition-all">How It Works</Button>
                </Link>
                <Link to="/pricing" className="block" onClick={handleMobileMenuClick}>
                  <Button variant="ghost" className="w-full justify-start text-gray-300 hover:text-aurora-teal hover:bg-hawk-dark-accent/50 transition-all">Pricing</Button>
                </Link>
                <div className="pt-4 space-y-2">
                  <Link to="/search" className="block" onClick={handleMobileMenuClick}>
                    <Button variant="ghost" className="w-full text-gray-300 hover:text-aurora-cyan hover:bg-hawk-dark-accent/50 transition-all">Event Search</Button>
                  </Link>
                  <Link to="/login" className="block" onClick={handleMobileMenuClick}>
                    <Button className="w-full bg-gradient-to-r from-aurora-teal to-aurora-cyan hover:from-aurora-cyan hover:to-aurora-teal text-white font-semibold py-2 rounded-lg shadow-lg">Sign In</Button>
                  </Link>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </nav>
  )
}